import { Timestamp } from '@bufbuild/protobuf';

import {
  ActionPopUpSingleButton,
  ActionsPopup,
  HomeworkPagination,
  HomeworkTimer,
  IQuestionStatus,
  IQuestionStatusObject,
  IStyles,
  IconWrapper,
  LinkButton,
  Loader,
  ProctoringConfig,
  QuestionContainerWithSolution,
  SecondaryButton,
  cloneObject,
  deserify,
  isCorrectAnswer,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
  useProctoring,
  getLastSignificantState,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import 'react-image-crop/src/ReactCrop.scss';

import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  AssessmentEnum,
  AssignmentConfigEnum,
  ContentAttempt,
  IdleTimeInformation,
  MarkingSchemeEnum,
  StudentAssessmentResponse,
  TaskStudentAttemptStatusEnum,
  TimeStampWithKey,
} from '@protos/learning_management/lms.db_pb';
import { HWStudentResponse } from '@protos/learning_management/lms.hw.student.apis_pb';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_STUDENT_DASHBOARD,
  HOMEWORK_SUBMITTED,
} from '../../../../routeHandling/RoutesNomenclature';
import { onHomeworkCardClick } from '../../../../utils/homework';
import { interactionEvent } from '../../../Auth/Login/login_logout.events';
import {
  setActiveHomeworSessionId,
  setActiveHomeworkAttemptResult,
  setActiveHomeworkStudentResponse,
  setAllAnswers,
  setProctoringViolationsSummary,
  setSelectedHomeworkData,
} from '../../reducer/homework.slice';
import {
  aiHomeworkCloseEvent,
  aiHomeworkOpenEvent,
  aiQuestionOpenEvent,
  aiQuestionSubmitEvent,
} from '../../utils_homework/homework.events';
import ActiveProgressbar from './ActiveProgressbar';
import { HwMemePage } from './HwMemePage';
import ImageUploadPopup from './ImageUploadPopup';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { checkFeatureData } from '../../utils_homework/homeworkUtil';
import { AssessmentStudentResponse } from '@protos/learning_management/lms.assessment.student.apis_pb';
import QuestionsSummaryPagination from './QuestionsSummaryPagination';
import React from 'react';
import { ProctoringPopups } from './ProctoringPopups';

const styles: IStyles = {
  root: {
    position: 'relative',
    boxSizing: 'border-box',
    p: {
      xs: `${pxToRem(15)} 0`,
      md: pxToRem(20),
      lg: `${pxTovW(40)} ${pxTovW(240)}`,
    },
    display: 'flex',
    justifyContent: 'space-between',
    m: 0,
  },
  heading: {
    mb: { xs: pxToRem(20), md: pxTovW(20) },
    px: { xs: pxToRem(8), md: 0 },
  },
  questionNumberBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: pxTovW(15),
    p: { xs: `${0} ${pxToRem(4)}`, md: `${pxTovW(20)} ${pxTovW(20)}` },
    boxSizing: 'border-box',
    mx: { xs: pxToRem(4), md: 0 },
    // border: { xs: '1px solid #4FB2FF', md: 'none' },
    borderRadius: { xs: pxToRem(8), md: 'none' },
  },
  questionBox: {
    margin: 'auto',
    // width: 'max-content',
    mt: { xs: pxToRem(20), md: pxTovW(0) },
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(8)}`,
      // md: `${pxTovW(20)} ${pxTovW(40)}`,
    },
    boxSizing: 'border-box',
  },
  submitButtonContainer: {
    margin: 'auto',
    // width: 'max-content',
    // mt: { xs: pxToRem(20), md: pxTovW(30) },
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(8)}`,
      md: `${pxTovW(20)} ${pxTovW(0)}`,
    },
    width: '100%',
    // width: { md: pxTovW(800), lg: pxTovW(600) },
    boxSizing: 'border-box',
  },
  submitButton: {
    background: '#FFE8E8',
    width: '100%',
    color: '#F54040',
    border: '1px solid #F54040',
    height: { xs: pxToRem(49), md: pxTovW(81), lg: pxTovW(61) },
    '&:hover': { background: '#FFD6D6' },
  },
};

export const ActiveHomework = () => {
  const NON_SEQUENTIAL = true;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const {
    LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { isOffline } = useDownloadContext();
  const [openUploadPopup, setOpenUploadPopup] = useState<boolean>(false);
  const homework_id =
    new URLSearchParams(location.search).get('assessmentId') || undefined;
  const sessionMode =
    new URLSearchParams(location.search).get('sessionMode') || undefined;

  const {
    active_homework_content,
    active_homework_student_response,
    active_homework_session_id,
    selected_homework_data,
    all_answers,
    proctoring_violations_summary,
  } = deserify(useAppSelector((state) => state.homework));
  const userInfo = deserify(useAppSelector((state) => state.auth.userInfo));
  const activeHomeworkContent =
    active_homework_content && homework_id
      ? active_homework_content[homework_id]
      : undefined;
  const activeHomeworkStudentResponse =
    active_homework_student_response && homework_id
      ? active_homework_student_response[homework_id]
      : undefined;
  const isJEEMarking = true;
  const taskQuestionInfo =
    activeHomeworkContent?.questionsSequenceInfo?.taskQuestionInfo || [];
  const sortedQuestionIds = taskQuestionInfo
    .sort((a, b) => a.sequence - b.sequence)
    .map((val) => val.questionId)
    .filter((val) => val !== undefined) as string[];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState<string>('');
  const [isPaused, setIsPaused] = useState(false);
  const [timeupPopup, setTimeupPopup] = useState(false);
  const [endTestPopup, setEndTestPopup] = useState(false);
  const [questionSummaryPopup, setQuestionSummaryPopup] = useState(false);
  const { selected_resource_info } = deserify(
    useAppSelector((state) => state.learn)
  );
  const selectedHomeworkContent = activeHomeworkContent?.assessmentContent.find(
    (hw) => hw.questionId == activeQuestionId
  );
  const activeQuestionIndex = sortedQuestionIds.indexOf(activeQuestionId);
  const totalQuestions = sortedQuestionIds.length;
  // const [answers, setAnswers] = useState<string[]>([]);
  const [startTime, setStartTime] = useState(Timestamp.fromDate(new Date()));
  const homework_end_path = useAppSelector(
    (state) => state.homework.homework_end_path
  );
  const initialTimerValue = useMemo(() => {
    return (
      (activeHomeworkContent?.assessment?.estimatedTimeInMin || 0) * 60 -
      (activeHomeworkStudentResponse?.attemptSpentTimeInSecs || 0)
    );
  }, []); // Run only on mount
  const [splitText, setSplitText] = useState<string | undefined>(undefined);

  const [showMeme, setShowMeme] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();

  const [showFullScreenPopup, setShowFullScreenPopup] = useState(false);
  const [showTabSwitchPopup, setShowTabSwitchPopup] = useState(false);
  const [showMultipleSessionPopup, setShowMultipleSessionPopup] =
    useState(false);
  const [showInactiveSessionPopup, setShowInactiveSessionPopup] =
    useState(false);
  // Track when input fields are focused to handle race conditions on mobile
  const lastInputFocusTimeRef = useRef<number>(0);
  const [questionStatusArray, setQuestionStatusArray] = useState<
    IQuestionStatusObject[]
  >(
    sortedQuestionIds.map((qId, index) => {
      return {
        index,
        statusInfo: IQuestionStatus.NotAttempted,
      };
    })
  );
  const [clearNonce, setClearNonce] = useState(0);
  const [isLastQuestionAttempted, setIsLastQuestionAttempted] =
    useState<boolean>(false);

  // --------------------------------------------------------------

  function canUseFullscreen(): boolean {
    const el = document.documentElement as any;

    return (
      Boolean(
        document.fullscreenEnabled ??
          (document as any).webkitFullscreenEnabled ??
          (document as any).mozFullScreenEnabled ??
          (document as any).msFullscreenEnabled
      ) &&
      Boolean(
        el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.mozRequestFullScreen ||
          el.msRequestFullscreen
      )
    );
  }

  // --------------------------------------------------------------

  // ======================== Proctoring Integration ========================

  const isProctoringEnabled = useMemo(() => {
    return (
      active_homework_content?.[
        homework_id || ''
      ]?.assessment?.assignmentInfo?.find(
        (info) =>
          info.configName === AssignmentConfigEnum.ASSIGNMENT_CONFIG_PROCTORING
      )?.isEnabled || false
    );
  }, [active_homework_content, homework_id]);

  const proctoringConfig: ProctoringConfig = {
    fullScreen: isProctoringEnabled, // useFullScreenEnforcement
    detectTabSwitch: isProctoringEnabled, // useTabSwitchDetection
    blockCopyPaste: isProctoringEnabled, // useCopyPasteRestriction
    blockRightClick: isProctoringEnabled, // useRightClickRestriction
    blockKeyboardShortcuts: isProctoringEnabled, // useKeyboardShortcutBlocking
    trackIdle: isProctoringEnabled, // useIdleTracking
    detectMultipleSessions: isProctoringEnabled, // useMultipleSessionDetection
  };

  // Helper function to check if user is interacting with an input field
  const isInputFieldActive = useCallback(() => {
    const activeElement = document.activeElement;
    if (!activeElement) return false;

    const tagName = activeElement.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable =
      activeElement.getAttribute('contenteditable') === 'true';

    // Check if it's an input field (input, textarea, or contenteditable)
    if (isInput || isContentEditable) {
      return true;
    }

    // Check if the active element is inside a contenteditable container
    const closestContentEditable = activeElement.closest(
      '[contenteditable="true"]'
    );
    if (closestContentEditable) {
      return true;
    }

    return false;
  }, []);

  // Track input field focus events to handle race conditions
  useEffect(() => {
    if (!isProctoringEnabled) return;

    const handleInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const tagName = target.tagName.toLowerCase();
      const isInput = tagName === 'input' || tagName === 'textarea';
      const isContentEditable =
        target.getAttribute('contenteditable') === 'true';
      const closestContentEditable = target.closest('[contenteditable="true"]');

      if (isInput || isContentEditable || closestContentEditable) {
        lastInputFocusTimeRef.current = Date.now();
      }
    };

    // Listen for focus events on input fields
    document.addEventListener('focusin', handleInputFocus, true);

    return () => {
      document.removeEventListener('focusin', handleInputFocus, true);
    };
  }, [isProctoringEnabled]);

  const proctoring = useProctoring(
    // ============ config control ============
    proctoringConfig,

    // ============ callbacks ============
    {
      // ----- useFullScreenEnforcement  -----
      onFullScreenExit: () => {
        // Ignore fullscreen exit if user is interacting with an input field
        // This prevents false positives when keyboard opens on mobile/tablet
        const isInputActive = isInputFieldActive();
        const recentInputFocus =
          Date.now() - lastInputFocusTimeRef.current < 500; // 500ms window

        if (isInputActive || recentInputFocus || !canUseFullscreen()) {
          return;
        }

        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            fullScreenExits: [
              ...(proctoring_violations_summary?.fullScreenExits || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
        setShowFullScreenPopup(true);
      },

      // ----- useTabSwitchDetection  -----
      onTabSwitch: (isHidden) => {
        if (isHidden) {
          // Ignore tab switch if user is interacting with an input field
          // This prevents false positives when keyboard opens on mobile/tablet
          const isInputActive = isInputFieldActive();
          const recentInputFocus =
            Date.now() - lastInputFocusTimeRef.current < 500; // 500ms window

          if (isInputActive || recentInputFocus) {
            return;
          }

          dispatch(
            setProctoringViolationsSummary({
              ...proctoring_violations_summary,
              tabSwitches: [
                ...(proctoring_violations_summary?.tabSwitches || []),
                Timestamp.fromDate(new Date()),
              ],
            })
          );
          setShowTabSwitchPopup(true);
        }
      },

      // ----- useCopyPasteRestriction  -----
      onCopyAttempt: (copyData) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            copyAttempts: [
              ...(proctoring_violations_summary?.copyAttempts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date(copyData.timestamp)),
                key: copyData.text,
              }),
            ],
          })
        );
      },
      onPasteAttempt: (pasteData) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            pasteAttempts: [
              ...(proctoring_violations_summary?.pasteAttempts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date(pasteData.timestamp)),
                key: pasteData.text,
              }),
            ],
          })
        );
      },

      // ----- useRightClickRestriction  -----
      onRightClickAttempt: () => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            rightClickAttempts: [
              ...(proctoring_violations_summary?.rightClickAttempts || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
      },

      // ----- useKeyboardShortcutBlocking  -----
      onBlockedShortcut: (key) => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            blockedShortcuts: [
              ...(proctoring_violations_summary?.blockedShortcuts || []),
              new TimeStampWithKey({
                timestamp: Timestamp.fromDate(new Date()),
                key: key,
              }),
            ],
          })
        );
      },

      // ----- useIdleTracking  -----
      onIdle: (idleTime) => {
        const now = Timestamp.fromDate(new Date());
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            idletimeData: [
              ...(proctoring_violations_summary?.idletimeData || []),
              new IdleTimeInformation({
                idleStartTime: now,
              }),
            ],
          })
        );
        setShowInactiveSessionPopup(true);
      },
      onActive: () => {
        const lastItem =
          proctoring_violations_summary?.idletimeData?.[
            proctoring_violations_summary?.idletimeData?.length - 1
          ];
        if (lastItem && !lastItem.idleEndTime) {
          const now = Timestamp.fromDate(new Date());
          dispatch(
            setProctoringViolationsSummary({
              ...proctoring_violations_summary,
              idletimeData: [
                ...(proctoring_violations_summary?.idletimeData || []).slice(
                  0,
                  -1
                ),
                new IdleTimeInformation({
                  idleStartTime: lastItem.idleStartTime,
                  idleEndTime: now,
                }),
              ],
            })
          );
        }
      },

      // ----- useMultipleSessionDetection  -----
      onMultipleSessionDetected: () => {
        dispatch(
          setProctoringViolationsSummary({
            ...proctoring_violations_summary,
            multipleSessionsData: [
              ...(proctoring_violations_summary?.multipleSessionsData || []),
              Timestamp.fromDate(new Date()),
            ],
          })
        );
        setShowMultipleSessionPopup(true);
      },
    },

    // ============ options ============
    {
      idleConfig: {
        idleThreshold: 60000,
        checkInterval: 1000,
      },
    }
  );

  // ----------------------------------------------------------------------

  const submitProctoringData = async () => {
    if (!proctoring_violations_summary) return;

    try {
      const proctoringResponse =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.createProctoringLog(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            homeworkSessionId: active_homework_session_id,
            questionId: activeQuestionId,
            fullScreenExits:
              proctoring_violations_summary?.fullScreenExits || [],
            tabSwitches: proctoring_violations_summary?.tabSwitches || [],
            copyAttempts: proctoring_violations_summary?.copyAttempts || [],
            pasteAttempts: proctoring_violations_summary?.pasteAttempts || [],
            rightClickAttempts:
              proctoring_violations_summary?.rightClickAttempts || [],
            blockedShortcuts:
              proctoring_violations_summary?.blockedShortcuts || [],
            idleTimeData: proctoring_violations_summary?.idletimeData || [],
            multipleSessions:
              proctoring_violations_summary?.multipleSessionsData || [],
          }
        );
      if (proctoringResponse.status === 200) {
        dispatch(setProctoringViolationsSummary(undefined));
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ----------------------------------------------------------------------

  const handleNonSeqNext = async (skip?: boolean, fromPagination?: boolean) => {
    await submitProctoringData();

    if (!homework_id || isNaN(Number(homework_id))) {
      return;
    }
    if (!active_homework_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }

    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      activeHomeworkStudentResponse
    );
    const responses = sortedQuestionIds.map((questionId) => {
      const existingResponse = activeHomeworkStudentResp.responses.find(
        (resp) => resp.questionId === questionId
      );
      if (existingResponse) {
        return existingResponse;
      } else {
        const questionTaskId = taskQuestionInfo.find(
          (val) => val.questionId === questionId
        )?.questionTaskId;
        return new StudentAssessmentResponse({
          questionId: questionId,
          questionTaskId: questionTaskId,
          responses: [],
          isAttempted: false,
        });
      }
    });
    // return;
    const lastValidAnswer = getLatestAnswer(activeQuestionId);
    if (skip) {
      dispatch(setAllAnswers([]));
    }
    const quesAttempt = new ContentAttempt({
      startTime: startTime,
      endTime: Timestamp.fromDate(new Date()),
      answer: skip ? [] : all_answers.length > 0 ? all_answers : [],
    });

    const activeQuestionResponseIndex = responses.findIndex(
      (resp) => resp.questionId === activeQuestionId
    );
    const updatedStatusArray = [...questionStatusArray];

    if (activeQuestionResponseIndex >= 0) {
      responses[activeQuestionResponseIndex].responses.push(quesAttempt);
      if (quesAttempt.answer.length > 0) {
        console.log('Marking question as attempted', { quesAttempt });
        responses[activeQuestionResponseIndex].isAttempted = true;
        //updating ques status array
        updatedStatusArray[activeQuestionIndex] = {
          ...updatedStatusArray[activeQuestionIndex],
          statusInfo: IQuestionStatus.Attempted,
        };
      } else {
        // if there is even one valid answer in previous attempts then mark attempted
        console.log(
          'No answer provided in this attempt. Checking for last valid answer.',
          { quesAttempt, lastValidAnswer }
        );
        responses[activeQuestionResponseIndex].isAttempted = lastValidAnswer
          ? true
          : false;
      }
    }
    // return;
    const timeDeltaInSeconds = Math.floor(
      (new Date().getTime() - startTime.toDate().getTime()) / 1000
    );

    const updatedElapsedTime =
      (selected_homework_data?.elapsedTime || 0) + timeDeltaInSeconds;

    try {
      setIsPaused(true);
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
            responses: responses,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_homework_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_homework_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse.status === 200) {
        setQuestionStatusArray(updatedStatusArray);
        // const studentHomeworkResp =
        //   await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
        //     {
        //       studentId: userInfo?.studentProfileId,
        //       assessmentId: Number(homework_id),
        //     }
        //   );
        dispatch(setAllAnswers([]));
        const oldValues = cloneObject(selected_homework_data);
        dispatch(
          setSelectedHomeworkData({
            ...oldValues,
            // elapsedTime: studentHomeworkResp.data?.attemptSpentTimeInSecs || 0, // Update the elapsed time in Redux
            elapsedTime: updatedElapsedTime, // Update the elapsed time in Redux
          })
        );
        const updatedResponse = new AssessmentStudentResponse(
          cloneObject(activeHomeworkStudentResp)
        );
        updatedResponse.responses = responses; // local responses just submitted
        updatedResponse.attemptSpentTimeInSecs = updatedElapsedTime;
        // selected_homework_data?.elapsedTime;
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: updatedResponse,
          })
        );
        const isLastQuestion = activeQuestionIndex === totalQuestions - 1;
        setIsPaused(false);
        if (!isLastQuestion) {
          setSplitText(undefined);
          setEndTestPopup(false);
          if (checkFeatureData({ userInfo: userInfo }).display_meme) {
            setShowMeme(true);
          } else memeEndHandler();
        } else if (isLastQuestion && !fromPagination) {
          setIsLastQuestionAttempted(true);
          setSplitText('Do you want to end the test?');
          setEndTestPopup(true);
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Question Submission failed',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          label: `${
            err.rawMessage
              ? err.rawMessage?.replace(/.*\]\s*/, '').trim()
              : err.message
              ? err.message.replace(/.*\]\s*/, '').trim()
              : 'Homework Submission Failed'
          }`,
          variant: 'error',
          open: true,
        })
      );
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------------------

  const createResponses = () => {
    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      cloneObject(activeHomeworkStudentResponse)
    );

    return sortedQuestionIds.map((questionId) => {
      // 1. Find existing response by ID ONLY
      const existingResponse = activeHomeworkStudentResp.responses.find(
        (resp) => resp.questionId === questionId
      );

      // 2. If it's NOT the active question, keep it as is or return a blank shell
      if (questionId !== activeQuestionId) {
        if (existingResponse) return existingResponse;

        const questionTaskId = taskQuestionInfo.find(
          (val) => val.questionId === questionId
        )?.questionTaskId;
        return new StudentAssessmentResponse({
          questionId: questionId,
          questionTaskId: questionTaskId,
          responses: [],
          isAttempted: false,
        });
      }

      // 3. LOGIC FOR ACTIVE QUESTION
      const history = existingResponse?.responses || [];
      const currentQuestion = selectedHomeworkContent;
      const lastAnswer = getLatestAnswer(activeQuestionId);
      let finalAnswer: string[] = [];
      const textTypes = [
        Question_QuestionEnum.QUESTION_TYPE_FIB,
        Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_LONG,
      ];
      if (
        currentQuestion?.questionType === undefined ||
        !textTypes.includes(currentQuestion.questionType)
      ) {
        // Only assign if NOT a text-based question
        finalAnswer = all_answers;
      }
      // Logic: Fallback to history only if UI is empty AND history provides an answer
      // (If history was cleared, lastAnswer will be null, so finalAnswer stays [])
      if (all_answers.length === 0 && lastAnswer) {
        finalAnswer = lastAnswer;
      }
      const quesAttempt = new ContentAttempt({
        startTime: startTime,
        endTime: Timestamp.fromDate(new Date()),
        answer: finalAnswer,
        isClearAnswer: false, // This is a navigation/save action, not a 'Clear' button click
      });

      const updatedResponse = existingResponse
        ? cloneObject(existingResponse)
        : new StudentAssessmentResponse({
            questionId: activeQuestionId,
            questionTaskId: taskQuestionInfo.find(
              (q) => q.questionId === activeQuestionId
            )?.questionTaskId,
            responses: [],
            isAttempted: false,
          });

      // Collate the history by appending the new attempt
      updatedResponse.responses = [...history, quesAttempt];
      updatedResponse.isAttempted = finalAnswer.length > 0;

      return updatedResponse;
    });
  };

  // ----------------------------------------------------------------------

  const submitBackHandler = async (isBackClicked?: boolean) => {
    let studentResponse: StudentAssessmentResponse[];
    if (NON_SEQUENTIAL) {
      studentResponse = createResponses();
    } else {
      const quesAttempt = new ContentAttempt({
        startTime: startTime,
        endTime: Timestamp.fromDate(new Date()),
        answer: [],
      });
      const questionTaskId = taskQuestionInfo.find(
        (val) => val.questionId === activeQuestionId
      )?.questionTaskId;
      const responseToSubmit = new StudentAssessmentResponse({
        // homeworkId: Number(homework_id),
        questionId: activeQuestionId,
        questionTaskId: questionTaskId,
        responses: [quesAttempt],
        isAttempted: false,
      });

      const activeHomeworkStudentResp = new AssessmentStudentResponse(
        activeHomeworkStudentResponse
      );
      const quesResponseIndex = activeHomeworkStudentResp.responses.findIndex(
        (resp) => resp.questionId === activeQuestionId
      );
      if (quesResponseIndex >= 0) {
        activeHomeworkStudentResp.responses[quesResponseIndex].responses.push(
          quesAttempt
        );
      } else {
        activeHomeworkStudentResp.responses.push(responseToSubmit);
      }
      studentResponse = activeHomeworkStudentResp.responses;
    }

    console.log('submit:', studentResponse);
    try {
      setIsPaused(true);
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            assessmentAttemptStatus: isBackClicked
              ? TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS
              : TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_TIMED_OUT,
            responses: studentResponse,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_homework_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_homework_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse) {
        if (!isBackClicked) {
          setTimeupPopup(true);
        }
      } else {
        if (!isBackClicked) {
          dispatch(
            setToastInfo({
              label: 'Something went wrong. Please try again.',
              variant: 'error',
              open: true,
            })
          );
          navigate(HOMEWORK_STUDENT_DASHBOARD);
        } else {
          dispatch(
            setToastInfo({
              label: 'Something went wrong. ',
              variant: 'error',
              open: true,
            })
          );
        }
      }
    } catch (err: any) {
      if (!isBackClicked) {
        dispatch(
          setToastInfo({
            label: 'Something went wrong. Please try again.',
            variant: 'error',
            open: true,
          })
        );
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      } else {
        dispatch(
          setToastInfo({
            label: 'Something went wrong. ',
            variant: 'error',
            open: true,
          })
        );
      }
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------------------

  const onBackClick = async () => {
    const studId = userInfo?.studentProfileId;
    if (studId) {
      await aiHomeworkCloseEvent({
        homeworkId: selected_homework_data?.homeworkId,
        isOffline: isOffline,
      });
      if (!showMeme) {
        submitBackHandler(true);
      }
      onHomeworkCardClick(
        dispatch,
        navigate,
        Number(homework_id),
        studId,
        undefined,
        homework_end_path,
        activeHomeworkContent?.assessment?.taskType,
        activeHomeworkContent?.assessment?.isMultiSubjectAssessmentFlow
      );
    }
  };

  // ----------------------------------------------------------------------

  const onLogoClick = async () => {
    const studId = userInfo?.studentProfileId;
    if (studId) {
      await aiHomeworkCloseEvent({
        homeworkId: selected_homework_data?.homeworkId,
        isOffline: isOffline,
      });
      submitBackHandler(true);
      navigate(HOME);
    }
  };

  // ----------------------------------------------------------------------

  const exitFullScreen = useCallback(() => {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen:', err);
      });
    }
  }, []);

  // ----------------------------------------------------------------------

  useEffect(() => {
    // const resumeCheck = completedTasks > 0 && completedTasks < totalTasks;
    // const reattemptedCheck = completedTasks >= totalTasks;

    (async () => {
      await aiHomeworkOpenEvent({
        homeworkId: selected_homework_data?.homeworkId,
        questionIds: selected_homework_data?.questionIds,
        isResume: sessionMode === '3' ? true : false,
        isReattempted: sessionMode === '2' ? true : false,
        homeworkType: activeHomeworkContent?.assessment?.taskType,
        isOffline: isOffline,

        // isResume:
        //   sessionMode === HomeworkSessionModeEnum.HOMEWORK_SESSION_MODE_RESUME
        //     ? true
        //     : false,
        // isReattempted:
        //   sessionMode ===
        //   HomeworkSessionModeEnum.HOMEWORK_SESSION_MODE_REATTEMPT
        //     ? true
        //     : false,

        // isResume: selected_homework_data?.resumeCheck,
        // isReattempted: selected_homework_data?.reattemptedCheck,

        // isResume: resumeCheck,
        // isReattempted: reattemptedCheck,
      });
    })();
    if (NON_SEQUENTIAL) {
      const newStatusArray = [...questionStatusArray];
      setActiveQuestionId(sortedQuestionIds[0]);
      dispatch(setAllAnswers([]));
      if (activeHomeworkStudentResponse?.responses) {
        activeHomeworkStudentResponse.responses.forEach((ans, i) => {
          const qIndex = sortedQuestionIds.findIndex(
            (val) => val === ans.questionId
          );

          if (qIndex !== -1) {
            //mark attempted if any attempt has answer
            const historyState = getLastSignificantState(ans.responses);
            newStatusArray[qIndex] = {
              ...newStatusArray[qIndex],
              statusInfo:
                historyState.status === 'lastAnswered'
                  ? IQuestionStatus.Attempted
                  : IQuestionStatus.NotAttempted,
            };
          }
        });
        setQuestionStatusArray(newStatusArray);
      }
    } else {
      if (
        activeHomeworkStudentResponse?.responses
        // &&
        // activeHomeworkStudentResponse?.responses.length !==
        //   activeHomeworkContent?.assessmentContent.length
      ) {
        const attemptedQuestionIds = activeHomeworkStudentResponse.responses
          .map((val) => (val.isAttempted && val.questionId) || '')
          .filter((val) => val !== '');
        const filteredQuestionIds = sortedQuestionIds.filter(
          (val) => !attemptedQuestionIds.includes(val)
        );
        // const lastQuestionAttempted =
        //   activeHomeworkStudentResponse.responses.length;
        if (filteredQuestionIds.length > 0) {
          activeHomeworkStudentResponse.responses.forEach((ans, i) => {
            const qIndex = sortedQuestionIds.findIndex(
              (val) => val === ans.questionId
            );
            questionStatusArray[qIndex].statusInfo = IQuestionStatus.Attempted;
          });
          setQuestionStatusArray(questionStatusArray);
          setActiveQuestionId(filteredQuestionIds[0]);
        }
      }
    }

    return () => {
      exitFullScreen();
    };
  }, []);

  // ----------------------------------------------------------------------

  useEffect(() => {
    setSelectedFunction(() => onBackClick);
    setSelectedLogoClickHandler(() => onLogoClick);
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [
    activeQuestionId,
    selected_homework_data?.elapsedTime,
    showMeme,
    activeHomeworkStudentResponse?.responses
      .map((resp) => resp.responses.length)
      .join(','),
  ]);

  // ----------------------------------------------------------------------

  const [resumeQueOpenTrigger, setResumeQueOpenTrigger] = useState(false);
  useEffect(() => {
    setShowMeme(false);
    // setAnswers([]);
    // dispatch(setAllAnswers([]));
    if (activeQuestionId) {
      const latestAnswer = getLatestAnswer(activeQuestionId);
      if (latestAnswer) {
        dispatch(setAllAnswers(latestAnswer)); // assuming setAllAnswers updates the state for `all_answers`
      } else {
        dispatch(setAllAnswers([])); // reset if no previous answer
      }
    }
    setStartTime(Timestamp.fromDate(new Date()));
    // Disabled so that we don't show previous ans
    // const quesResponse = activeHomeworkStudentResponse?.responses.find(resp => resp.questionId == activeQuestionId);
    // setAnswers(new ContentAttempt(getLastAttemptedResponse(quesResponse?.responses)).answer);

    (async () => {
      if (sessionMode === '3' && resumeQueOpenTrigger) {
        await aiQuestionOpenEvent({
          homeworkId: selected_homework_data?.homeworkId,
          questionId: activeQuestionId,
          isOffline: isOffline,
        });
      } else if (sessionMode !== '3') {
        await aiQuestionOpenEvent({
          homeworkId: selected_homework_data?.homeworkId,
          questionId: activeQuestionId,
          isOffline: isOffline,
        });
      }
      setResumeQueOpenTrigger(true);
    })();
  }, [activeQuestionId]);

  // ----------------------------------------------------------------------

  const getLatestAnswer = (questionId: string) => {
    const responseObj = activeHomeworkStudentResponse?.responses?.find(
      (e) => e.questionId === questionId
    );

    const history = responseObj?.responses || [];

    // Use the new helper to get a definitive snapshot of the question state
    const historyState = getLastSignificantState(history);

    // If the timeline shows the last significant action was an answer, return it.
    // If it was 'cleared' or 'null', return null.
    if (historyState.status === 'lastAnswered') {
      return historyState.answer;
    }

    return null;
  };

  // ----------------------------------------------------------------------

  useEffect(() => {
    (async () => {
      if (isSubmitting) {
        if (!NON_SEQUENTIAL) {
          await handleNext();
        } else {
          await handleNonSeqNext();
        }

        setIsSubmitting(false);
      }
    })();
  }, [isSubmitting]);

  // useEffect(() => {
  //   if (!activeHomeworkContent?.homework?.estimatedTimeInMin) return;

  //   const totalTimeInSeconds =
  //     activeHomeworkContent.homework.estimatedTimeInMin * 60;

  //   // Initialize current time from Redux state or total time
  //   let currentTime = homework_time_remaining || totalTimeInSeconds;

  //   const timer = setInterval(() => {
  //     currentTime -= 1;

  //     if (currentTime <= 0) {
  //       clearInterval(timer);
  //       // Perform actions on timer completion
  //       handleNext();
  //       navigate(`${HOMEWORK_SUBMITTED}?homeworkId=${homework_id}`);
  //       dispatch(setHomeworkTimeRemaining(0)); // Update Redux state to 0
  //     } else {
  //       dispatch(setHomeworkTimeRemaining(currentTime)); // Update Redux state with remaining time
  //     }
  //   }, 1000);

  //   // Cleanup interval on component unmount or dependency change
  //   return () => {
  //     clearInterval(timer);
  //   };
  // }, [
  //   activeHomeworkContent?.homework?.estimatedTimeInMin,
  //   homework_time_remaining,
  //   dispatch,
  // ]);

  // ----------------------------------------------------------------------

  const homeworkSubmit = async () => {
    if (activeQuestionIndex < 0 || !homework_id || isNaN(Number(homework_id))) {
      return;
    }
    if (!active_homework_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    let activeStudentResponse: StudentAssessmentResponse[];

    if (isLastQuestionAttempted) {
      activeStudentResponse = new AssessmentStudentResponse(
        activeHomeworkStudentResponse
      ).responses;
    } else {
      activeStudentResponse = createResponses();
    }

    console.log('responses', isLastQuestionAttempted, activeStudentResponse);
    try {
      setIsPaused(true);
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED,
            responses: activeStudentResponse,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_homework_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_homework_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse.status === 200) {
        const activeHomeworkAttemptResult = hwSubmitResponse.attemptResult;
        dispatch(
          setActiveHomeworkAttemptResult({
            homeworkId: Number(homework_id),
            result: activeHomeworkAttemptResult,
          })
        );
        setIsPaused(true);
        dispatch(setActiveHomeworSessionId(undefined));
        navigate(`${HOMEWORK_SUBMITTED}?assessmentId=${homework_id}`);
      } else {
        dispatch(
          setToastInfo({
            label: 'Homework Submission failed',
            variant: 'error',
            open: true,
          })
        );
      }
      setIsPaused(false);
    } catch (err: any) {
      setIsPaused(false);
      dispatch(
        setToastInfo({
          label: `${
            err.rawMessage
              ? err.rawMessage?.replace(/.*\]\s*/, '').trim()
              : err.message
              ? err.message.replace(/.*\]\s*/, '').trim()
              : 'Homework Submission Failed'
          }`,
          variant: 'error',
          open: true,
        })
      );
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------------------

  const handleNext = async () => {
    await submitProctoringData();

    if (activeQuestionIndex < 0 || !homework_id || isNaN(Number(homework_id))) {
      return;
    }
    if (!active_homework_session_id) {
      // throw new Error('Invalid or missing session id');
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    const isLastQuestion = activeQuestionIndex === totalQuestions - 1;
    const quesAttempt = new ContentAttempt({
      startTime: startTime,
      endTime: Timestamp.fromDate(new Date()),
      answer: all_answers,
    });

    const questionTaskId = taskQuestionInfo.find(
      (val) => val.questionId === activeQuestionId
    )?.questionTaskId;

    const responseToSubmit = new StudentAssessmentResponse({
      // homeworkId: Number(homework_id),
      questionId: activeQuestionId,
      questionTaskId: questionTaskId,
      responses: [quesAttempt],
      isAttempted: true,
    });

    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      activeHomeworkStudentResponse
    );
    const quesResponseIndex = activeHomeworkStudentResp.responses.findIndex(
      (resp) => resp.questionId === activeQuestionId
    );
    if (quesResponseIndex >= 0) {
      activeHomeworkStudentResp.responses[quesResponseIndex].responses.push(
        quesAttempt
      );
    } else {
      activeHomeworkStudentResp.responses.push(responseToSubmit);
    }
    try {
      setIsPaused(true);
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            assessmentAttemptStatus: isLastQuestion
              ? TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED
              : TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
            responses: activeHomeworkStudentResp.responses,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_homework_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_homework_data?.elapsedTime
              : undefined,
          }
        );
      if (hwSubmitResponse.status === 200) {
        // dispatch(
        //   setActiveHomeworkStudentResponse({
        //     homeworkId: Number(homework_id),
        //     response: activeHomeworkStudentResp,
        //   })
        // );
        questionStatusArray[activeQuestionIndex].statusInfo =
          IQuestionStatus.Attempted;
        setQuestionStatusArray(questionStatusArray);

        const studentHomeworkResp =
          await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
            {
              studentId: userInfo?.studentProfileId,
              assessmentId: Number(homework_id),
            }
          );

        const filteredSubmitResponse = studentHomeworkResp.data?.responses.find(
          (response) => response.questionId === activeQuestionId
        );

        if (
          [
            'veryShortQuesContentModel',
            'shortQuesContentModel',
            'longQuesContentModel',
            'fibContentModel',
            'mcqMultipleContentModel',
            'cbqQuesContentModel',
          ].includes(selectedHomeworkContent?.question?.model?.case || '')
        ) {
          // interaction event "NEXT" if subjective &
          await interactionEvent({
            url: '',
            context: 'homework',
            name: 'NEXT',
            pathSegments: pathSegments,
            isOffline: isOffline,
          });
        }

        await aiQuestionSubmitEvent({
          questionId: activeQuestionId,
          homeworkId: Number(homework_id),
          submitStatus: filteredSubmitResponse?.isCorrect,
          answer: all_answers,
          isOffline: isOffline,
        });

        dispatch(setAllAnswers([]));
        const oldValues = cloneObject(selected_homework_data);
        dispatch(
          setSelectedHomeworkData({
            ...oldValues,
            elapsedTime: studentHomeworkResp.data?.attemptSpentTimeInSecs || 0, // Update the elapsed time in Redux
          })
        );
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: studentHomeworkResp.data,
          })
        );
        setIsPaused(false);
        if (!isLastQuestion) {
          if (
            checkFeatureData({ userInfo: userInfo }).display_meme &&
            activeHomeworkContent?.assessment?.assessmentType !==
              AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT && // Skip meme for diagnostic assessments
            activeHomeworkContent?.assessment?.assessmentType !==
              AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT // Skip meme for formative assessments
          )
            setShowMeme(true);
          else memeEndHandler();
        } else if (isLastQuestion) {
          const activeHomeworkAttemptResult = hwSubmitResponse.attemptResult;
          dispatch(
            setActiveHomeworkAttemptResult({
              homeworkId: Number(homework_id),
              result: activeHomeworkAttemptResult,
            })
          );
          setIsPaused(true);
          dispatch(setActiveHomeworSessionId(undefined));
          navigate(`${HOMEWORK_SUBMITTED}?assessmentId=${homework_id}`);
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Question Submission failed',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          label: `${
            err.rawMessage
              ? err.rawMessage?.replace(/.*\]\s*/, '').trim()
              : err.message
              ? err.message.replace(/.*\]\s*/, '').trim()
              : 'Homework Submission Failed'
          }`,
          variant: 'error',
          open: true,
        })
      );
      if (err.rawMessage.includes('deadline_exceeded')) {
        navigate(HOMEWORK_STUDENT_DASHBOARD);
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------------------------------------------------------------

  const handleAnswerChange = (val: string, ind?: number) => {
    // let answersCopy = structuredClone(all_answers);
    let answersCopy = cloneObject(all_answers);
    switch (selectedHomeworkContent?.questionType) {
      case Question_QuestionEnum.QUESTION_TYPE_MCQM:
        if (!answersCopy.includes(val)) {
          answersCopy.push(val);
        } else {
          answersCopy = answersCopy.filter((v) => v !== val);
        }
        break;
      case Question_QuestionEnum.QUESTION_TYPE_FIB:
        if (typeof ind == 'number') {
          if (ind >= 1) {
            for (let i = 0; i < ind; i++) {
              if (!answersCopy[i]) {
                answersCopy[i] = '';
              }
            }
          }
          answersCopy[ind] = val;
        }
        break;
      case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      case Question_QuestionEnum.QUESTION_TYPE_TF:
        answersCopy = [val];
        break;
      case Question_QuestionEnum.QUESTION_TYPE_LONG:
      case Question_QuestionEnum.QUESTION_TYPE_SHORT:
      case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
      case Question_QuestionEnum.QUESTION_TYPE_CBQ:
        answersCopy = [val];
        break;
    }
    // setAnswers(answersCopy);
    dispatch(setAllAnswers(answersCopy));
    // Move to next question if tf or mcq-s
    switch (selectedHomeworkContent?.questionType) {
      case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      case Question_QuestionEnum.QUESTION_TYPE_TF:
        setIsSubmitting(true);
        break;
      default:
        break;
    }
  };

  // ----------------------------------------------------------------------

  const memeEndHandler = () => {
    if (!NON_SEQUENTIAL) {
      const attemptedQuestionIds =
        activeHomeworkStudentResponse?.responses
          .map((val) => (val.isAttempted && val.questionId) || '')
          .filter((val) => val !== '') || [];
      const filteredQuestionIds = sortedQuestionIds
        .slice(activeQuestionIndex + 1)
        .filter((val) => !attemptedQuestionIds.includes(val));
      setActiveQuestionId(filteredQuestionIds[0]);
    } else setActiveQuestionId(sortedQuestionIds[activeQuestionIndex + 1]);
  };

  // ----------------------------------------------------------------------

  const studentResponseForCurrentQuestion = new HWStudentResponse(
    activeHomeworkStudentResponse
  ).responses.find((resp) => resp.questionId === activeQuestionId);
  //^
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClearAnswer = async () => {
    if (!activeQuestionId || !homework_id || isNaN(Number(homework_id))) return;
    if (!active_homework_session_id) {
      dispatch(
        setToastInfo({
          label: 'Invalid or missing Session id',
          variant: 'error',
          open: true,
        })
      );
      return;
    }

    setIsPaused(true); // Stop timer and prevent navigation clicks
    dispatch(setAllAnswers([])); // Clear local UI state immediately
    setClearNonce((prev) => prev + 1);

    const prevStudentResp = new AssessmentStudentResponse(
      cloneObject(activeHomeworkStudentResponse)
    );
    const activeHomeworkStudentResp = new AssessmentStudentResponse(
      cloneObject(activeHomeworkStudentResponse)
    );
    const snapshotTimestamp = Timestamp.fromDate(new Date());

    // Create clear attempt (recorded as an explicit user action)
    const clearAttempt = new ContentAttempt({
      startTime: startTime,
      endTime: Timestamp.fromDate(new Date()),
      answer: [],
      isClearAnswer: true,
    });

    // Preserve history for all questions; update only the active question
    const updatedResponses = sortedQuestionIds.map((id) => {
      const existing = activeHomeworkStudentResp.responses.find(
        (r) => r.questionId === id
      );
      const questionTaskId = taskQuestionInfo.find(
        (q) => q.questionId === id
      )?.questionTaskId;

      if (id !== activeQuestionId) {
        return (
          existing ||
          new StudentAssessmentResponse({
            questionId: id,
            questionTaskId,
            responses: [] as ContentAttempt[],
            isAttempted: false,
          })
        );
      }
      const updated = existing
        ? cloneObject(existing)
        : new StudentAssessmentResponse({
            questionId: id,
            questionTaskId,
            responses: [] as ContentAttempt[],
            isAttempted: false,
          });

      updated.responses = [...(updated.responses || []), clearAttempt];
      updated.isAttempted = false;
      return updated;
    });

    // Update the Question Status Array
    const updatedStatusArray = [...questionStatusArray];
    if (activeQuestionIndex >= 0) {
      updatedStatusArray[activeQuestionIndex] = {
        ...updatedStatusArray[activeQuestionIndex],
        statusInfo: IQuestionStatus.NotAttempted,
      };
      setQuestionStatusArray(updatedStatusArray);
    }

    // Optimistically update Redux so `getLatestAnswer` immediately reflects CLEAR
    const finalFullResponse = cloneObject(activeHomeworkStudentResp);
    finalFullResponse.responses = updatedResponses;
    dispatch(
      setActiveHomeworkStudentResponse({
        homeworkId: Number(homework_id),
        response: finalFullResponse,
      })
    );
    try {
      setIsLoading(true);
      const hwSubmitResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.submitStudentAssessmentResponse(
          {
            studentId: userInfo?.studentProfileId,
            assessmentId: Number(homework_id),
            assessmentAttemptStatus:
              TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_IN_PROGRESS,
            responses: updatedResponses,
            timestamp: Timestamp.fromDate(new Date()),
            sessionId: active_homework_session_id,
            attemptSpentTimeInSecs: checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed
              ? selected_homework_data?.elapsedTime
              : undefined,
          }
        );

      if (hwSubmitResponse.status !== 200) {
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: prevStudentResp,
          })
        );
        dispatch(
          setToastInfo({
            label: 'Failed to clear answer. Please try again.',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err: any) {
      // Revert optimistic redux update if API fails
      dispatch(
        setActiveHomeworkStudentResponse({
          homeworkId: Number(homework_id),
          response: prevStudentResp,
        })
      );
      dispatch(
        setToastInfo({
          label: err?.message || 'Failed to clear answer. Please try again.',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setIsLoading(false);
      setIsPaused(false);
    }
  };
  if (showMeme)
    return (
      <HwMemePage
        isCorrectAnswer={isCorrectAnswer(
          new Question(selectedHomeworkContent),
          // answers
          all_answers
        )}
        timeTaken={
          (new Date().getTime() - new Date(Number(startTime)).getTime()) / 1000
        }
        actualQuestionTime={
          selectedHomeworkContent?.question?.model.value?.commonQuestionContent
            ?.time
        }
        memeEndHandler={memeEndHandler}
        onClickOnly={async () => {
          await interactionEvent({
            url: '',
            context: 'meme_page',
            name: 'NEXT_QUESTION',
            pathSegments: pathSegments,
            isOffline: isOffline,
          });
        }}
      />
    );

  // ----------------------------------------------------------------------

  const scanClickHandler = async () => {
    setOpenUploadPopup(true);
    await interactionEvent({
      url: '',
      context: 'homework',
      name: 'SCAN',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  // ----------------------------------------------------------------------

  const handleTimeUpdate = (remainingTime: number) => {
    if (remainingTime < 0) {
      return;
    }
    if (activeHomeworkContent?.assessment?.estimatedTimeInMin) {
      const oldValues = cloneObject(selected_homework_data);
      const newElapsedTimeinSecs =
        activeHomeworkContent?.assessment?.estimatedTimeInMin * 60 -
        remainingTime;
      if (
        newElapsedTimeinSecs <=
        activeHomeworkContent?.assessment?.estimatedTimeInMin * 60
      ) {
        const cloneActiveHomeworkStudentResp = new AssessmentStudentResponse(
          cloneObject(activeHomeworkStudentResponse)
        );
        cloneActiveHomeworkStudentResp.attemptSpentTimeInSecs =
          newElapsedTimeinSecs;
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: cloneActiveHomeworkStudentResp,
          })
        );
        dispatch(
          setSelectedHomeworkData({
            ...oldValues,
            elapsedTime: newElapsedTimeinSecs, // Update the elapsed time in Redux
          })
        );
      }
    }
  };

  // -------------------------------------------------------------

  const setActiveQuestion = (pageNumber: number) => {
    if (!pageNumber) {
      return;
    }
    const pageIndex = pageNumber - 1;
    setActiveQuestionId(sortedQuestionIds[pageIndex]);
  };

  // -------------------------------------------------------------

  const skipQuestionClickHandler = async (questionIndex: number) => {
    await handleNonSeqNext(true);
    const isLastQuestion = questionIndex === totalQuestions - 1;
    if (isLastQuestion) {
      setActiveQuestionId(sortedQuestionIds[0]);
    } else setActiveQuestionId(sortedQuestionIds[questionIndex + 1]);
  };

  // -------------------------------------------------------------

  // if (isLoading) return <Loader />;

  return (
    <Grid
      container
      spacing={0}
      sx={{
        ...styles.root,
        pointerEvents: isLoading ? 'none' : undefined,
        opacity: isLoading ? 0.5 : showFullScreenPopup ? 0 : 1,
      }}
    >
      {/* <ActiveProgressbar
        percentage={((activeQuestionIndex + 1) * 100) / totalQuestions}
      /> */}
      <Grid item xs={12} md={4.5} sx={styles.heading}>
        <Box
          sx={{
            display: 'flex',
            // flexDirection: 'column',
            gap: { xs: pxToRem(6), md: pxTovW(250) },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              width: { xs: '100%', md: pxTovW(550), lg: pxTovW(450) },
              wordWrap: 'break-word',
            }}
          >
            {activeHomeworkContent?.assessment?.assessmentTitle}
          </Typography>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {checkFeatureData({
              assignmentConfigInfo:
                activeHomeworkContent?.assessment?.assignmentInfo,
              userInfo: userInfo,
            }).timed &&
              isMobile && (
                <HomeworkTimer
                  // initialTime={
                  //   (activeHomeworkContent?.assessment?.estimatedTimeInMin || 0) *
                  //     60 -
                  //   (activeHomeworkStudentResponse?.attemptSpentTimeInSecs || 0)
                  // }
                  initialTime={initialTimerValue}
                  onTimeUpdate={handleTimeUpdate}
                  isPaused={isPaused}
                  onTestEnd={() => {
                    submitBackHandler();
                  }}
                />
              )}
          </Box>
        </Box>
        {/* <Box sx={{ p: { md: 20 } }}>
          {checkFeatureData({
            assignmentConfigInfo:
              activeHomeworkContent?.assessment?.assignmentInfo,
            userInfo: userInfo,
          }).timed && (
            <HomeworkTimer
              initialTime={
                (activeHomeworkContent?.assessment?.estimatedTimeInMin || 0) *
                  60 -
                (activeHomeworkStudentResponse?.attemptSpentTimeInSecs || 0)
              }
              onTimeUpdate={handleTimeUpdate}
              isPaused={isPaused}
              onTestEnd={() => {
                submitBackHandler();
              }}
            />
          )}
        </Box> */}
      </Grid>
      <Grid container sx={{ padding: 0 }}>
        {/* <Box
          sx={{
            overflowX: 'scroll',
            px: { xs: pxToRem(8) },
            overflowY: 'clip',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <HomeworkPagination
            currentPage={activeQuestionIndex + 1}
            totalPages={totalQuestions}
            showPreviousNextButtons={false}
            questionStatusArray={questionStatusArray}
            onPageChange={(page) => setActiveQuestion(page)}
            maxButtonsToDisplay={10}
          />
        </Box> */}
        <Grid
          item
          xs={12}
          md={3.5}
          sx={{
            order: { xs: 1, md: 2 },
          }}
        >
          <Box sx={styles.questionNumberBox}>
            <Box sx={{ width: '100%' }}>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                paddingY={1}
              >
                <Box
                  width={'100%'}
                  display={'flex'}
                  justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                  alignItems={'center'}
                  gap={1}
                >
                  <Typography variant="h5" color="neutral.duskySteel">
                    Completed{' '}
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {getCompletionPercentage(questionStatusArray)}
                    {'%'}
                  </Typography>
                </Box>
                <Box display={{ xs: 'block', md: 'none' }}>
                  <Button
                    sx={{
                      width: 'max-content',
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      fontSize: { xs: pxToRem(10) },
                    }}
                    onClick={() => setQuestionSummaryPopup(true)}
                  >
                    View Details
                  </Button>
                </Box>
              </Box>
              <ActiveProgressbar
                percentage={getCompletionPercentage(questionStatusArray)}
              />
            </Box>
            <Box>
              {checkFeatureData({
                assignmentConfigInfo:
                  activeHomeworkContent?.assessment?.assignmentInfo,
                userInfo: userInfo,
              }).timed &&
                !isMobile && (
                  <HomeworkTimer
                    // initialTime={
                    //   (activeHomeworkContent?.assessment?.estimatedTimeInMin ||
                    //     0) *
                    //     60 -
                    //   (activeHomeworkStudentResponse?.attemptSpentTimeInSecs || 0)
                    // }
                    initialTime={initialTimerValue}
                    onTimeUpdate={handleTimeUpdate}
                    isPaused={isPaused}
                    onTestEnd={() => {
                      submitBackHandler();
                    }}
                  />
                )}
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <QuestionsSummaryPagination
                currentPage={activeQuestionIndex + 1}
                totalPages={totalQuestions}
                questionStatusArray={questionStatusArray}
                onPageChange={async (page) => {
                  await handleNonSeqNext(true, true);
                  setActiveQuestion(page);
                }}
              />
              {NON_SEQUENTIAL && (
                <Box sx={styles.submitButtonContainer}>
                  <Button
                    sx={styles.submitButton}
                    onClick={() => setEndTestPopup(true)}
                  >
                    End Test
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={8.5} sx={{ order: { xs: 2, md: 1 } }}>
          {selectedHomeworkContent && (
            <Box sx={styles.questionBox}>
              <QuestionContainerWithSolution
                key={`${activeQuestionId}-${clearNonce}`}
                userAttemptedAnswer={
                  all_answers && all_answers.length
                    ? all_answers
                    : getLatestAnswer(activeQuestionId) ?? // Check if the answer is not empty
                      undefined
                }
                questionNumber={activeQuestionIndex + 1}
                question={new Question(selectedHomeworkContent)}
                handleAnswerChange={handleAnswerChange}
                handleNext={NON_SEQUENTIAL ? handleNonSeqNext : handleNext}
                showNextButton
                showHintIcon
                homeworkAttempt={true}
                scanClickHandler={scanClickHandler}
                hiddenScanButton={isProctoringEnabled}
                selected_resource_info={selected_resource_info}
                disableInput={isSubmitting}
                eventCollection={{
                  hintBoxOpen: async () => {
                    await interactionEvent({
                      url: '',
                      context: 'popup',
                      name: 'CLUE',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  },
                  hintBoxOk: async () => {
                    await interactionEvent({
                      url: '',
                      context: 'clue',
                      name: 'OK',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  },
                  answerSelection: async () => {
                    await interactionEvent({
                      url: '',
                      context: 'question',
                      name: 'ANSWER_SELECTION',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  },
                }}
                imageResizeEnabled={true}
                questionTypeDisplay={true}
                showQuestionStats
                clearAnswerDisplay={
                  activeHomeworkContent?.assessment?.markingSchemeType ===
                  MarkingSchemeEnum.MARKING_SCHEME_JEE
                    ? true
                    : false
                }
                isClearAnswerDisabled={
                  isPaused ||
                  ((!all_answers || all_answers.length === 0) &&
                    getLastSignificantState(
                      activeHomeworkStudentResponse?.responses?.find(
                        (r) => r.questionId === activeQuestionId
                      )?.responses || []
                    ).status !== 'lastAnswered')
                }
                clearAnswerClickHandler={handleClearAnswer}
                hideBloomLevel={
                  activeHomeworkContent?.assessment?.markingSchemeType ===
                  MarkingSchemeEnum.MARKING_SCHEME_JEE
                    ? true
                    : false
                }
              />
            </Box>
          )}
          <Box component="div" display={'flex'} justifyContent={'center'}>
            <LinkButton
              textColor={'primary.main'}
              onClick={() => skipQuestionClickHandler(activeQuestionIndex)}
            >
              Skip Question
            </LinkButton>
          </Box>
          <Box
            component="div"
            display={{ xs: 'flex', md: 'none' }}
            justifyContent={'center'}
            p={2}
          >
            {NON_SEQUENTIAL && (
              <Box sx={styles.submitButtonContainer}>
                <Button
                  sx={styles.submitButton}
                  onClick={() => setEndTestPopup(true)}
                >
                  End Test
                </Button>
              </Box>
            )}
          </Box>
          {/* {NON_SEQUENTIAL && (
            <Box sx={styles.submitButton}>
              <SecondaryButton onClick={homeworkSubmit}>
                Submit Homework
              </SecondaryButton>
            </Box>
          )} */}
        </Grid>
        {/* - - - - - - - - - - - - - - - - - - - - - - - - */}
      </Grid>

      <ImageUploadPopup
        open={openUploadPopup}
        handleClose={() => setOpenUploadPopup(false)}
        studentId={userInfo?.studentProfileId}
        homeworkId={Number(homework_id)}
        sessionId={active_homework_session_id}
        questionId={selectedHomeworkContent?.questionId}
        handleAnswerChange={handleAnswerChange}
      />

      <ActionPopUpSingleButton
        fontSmall
        open={timeupPopup}
        iconName="info_red"
        popupText="The allotted time for the assessment has ended."
        buttontext="Ok"
        ctaHandler={() => {
          setTimeupPopup(false);
          navigate(HOME);
        }}
        background="#0AA34F"
        textcolor="white"
      />

      <ActionsPopup
        open={endTestPopup}
        handleClose={() => {
          setEndTestPopup(false);
        }}
        closeIcon
        fontSmall
        iconName="search"
        popupText={'You have answered all the questions.'}
        splitText={splitText ? splitText : 'Would you like to submit the test?'}
        popupTextColor="primary.main"
        splitTextColor="black"
        yesClickHandler={homeworkSubmit}
        noClickHandler={async () => {
          setSplitText(undefined);
          setEndTestPopup(false);
        }}
        noText={splitText ? 'REVIEW TEST' : 'GO BACK TO TEST'}
        yesText="END TEST"
        customComponent={
          getUnattemptCount(questionStatusArray) > 0
            ? EndTestPopupContent({
                not_answered: getUnattemptCount(questionStatusArray),
                total: totalQuestions,
              })
            : undefined
        }
        buttonVariant="greenContained"
      />

      <Modal
        open={questionSummaryPopup}
        onClose={() => setQuestionSummaryPopup(false)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            // height: '100vh',
            width: '100vw',
            padding: { xs: pxToRem(10), md: pxToRem(0) },
            boxSizing: 'border-box',
          }}
        >
          <QuestionsSummaryPagination
            currentPage={activeQuestionIndex + 1}
            totalPages={totalQuestions}
            questionStatusArray={questionStatusArray}
            onPageChange={async (page) => {
              await handleNonSeqNext(true, true);
              setActiveQuestion(page);
            }}
          />
          <Box>
            <Button
              onClick={() => setQuestionSummaryPopup(false)}
              sx={{ p: pxToRem(10) }}
            >
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* <ActionsPopup
        open={showExitPopup}
        iconName="homework"
        popupText="Exiting now will erase all your current homework progress. Confirm?"
        handleClose={() => handleExit('no')}
        noClickHandler={() => handleExit('no')}
        yesClickHandler={() => handleExit('yes')}
        fontSmall
      /> */}

      <ProctoringPopups
        showFullScreenPopup={showFullScreenPopup}
        setShowFullScreenPopup={setShowFullScreenPopup}
        enterFullScreenMode={() => {
          proctoring.fullScreen.requestFullScreen();
          setShowFullScreenPopup(false);
        }}
        showTabSwitchPopup={showTabSwitchPopup}
        setShowTabSwitchPopup={setShowTabSwitchPopup}
        enterTabSwitchMode={() => {
          setShowTabSwitchPopup(false);
        }}
        showMultipleSessionPopup={showMultipleSessionPopup}
        setShowMultipleSessionPopup={setShowMultipleSessionPopup}
        enterMultipleSessionMode={() => {
          setShowMultipleSessionPopup(false);
        }}
        showInactiveSessionPopup={showInactiveSessionPopup}
        setShowInactiveSessionPopup={setShowInactiveSessionPopup}
        enterInactiveSessionMode={() => {
          setShowInactiveSessionPopup(false);
        }}
      />
    </Grid>
  );
};

// -------------------------------------------------------------

function splitQuestionIds(questionIds: string[], activeQuestionId: string) {
  // Find the index of the activeQuestionId in the sorted list
  const activeIndex = questionIds.indexOf(activeQuestionId);
  // If activeQuestionId is not in the list, return empty arrays
  if (activeIndex === -1) {
    return [[], []];
  }
  // Slice the list into two parts
  const idsBeforeAndIncludingActive = questionIds.slice(0, activeIndex + 1);
  const idsAfterActive = questionIds.slice(activeIndex + 1);

  return [idsBeforeAndIncludingActive, idsAfterActive];
}

// -------------------------------------------------------------

export function getCompletionPercentage(
  questionStatusArray: IQuestionStatusObject[]
): number {
  if (!questionStatusArray.length) return 0;

  const attemptedCount = questionStatusArray.filter(
    (q) =>
      q.statusInfo === IQuestionStatus.Attempted ||
      q.statusInfo === IQuestionStatus.Correct ||
      q.statusInfo === IQuestionStatus.Incorrect
  ).length;

  const percentage = (attemptedCount / questionStatusArray.length) * 100;

  return Math.round(percentage); // Rounded to nearest integer
}

export function getUnattemptCount(
  questionStatusArray: IQuestionStatusObject[]
): number {
  if (!questionStatusArray.length) return 0;

  const notAttemptedCount = questionStatusArray.filter(
    (q) => q.statusInfo === IQuestionStatus.NotAttempted
  ).length;

  return notAttemptedCount;
}

// -------------------------------------------------------------

// const getSplitText = ({
//   notAttemptCount,
//   total,
// }: {
//   notAttemptCount: number;
//   total: number;
// }) => {
//   return notAttemptCount
//     ? `You still haven’t answered ${notAttemptCount} out of ${total} questions.`
//     : undefined;
// };
interface ICProps {
  not_answered: number;
  total: number;
}

export const EndTestPopupContent: React.FC<ICProps> = ({
  not_answered,
  total,
}) => {
  const answered = total - not_answered;

  const rows = [
    { label: 'Total', value: total },
    { label: 'Answered', value: answered },

    { label: 'Not Answered', value: not_answered },
  ];

  return (
    <Box
      sx={{
        textAlign: 'center',
        paddingBottom: pxToRem(20),
      }}
    >
      <Typography variant="h2" color="primary.main" mb={1}>
        You Have:
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content max-content',
          justifyContent: 'center',
          rowGap: 1,
          columnGap: 1,
          py: pxToRem(20),
        }}
      >
        {rows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Typography variant="h2" textAlign="left">
              {label}
            </Typography>
            <Typography variant="h2" textAlign="center">
              :
            </Typography>
            <Typography variant="h2" textAlign="left">
              {value}{' '}
              <Box
                component="span"
                sx={{ fontFamily: 'lato', fontWeight: 100 }}
              >
                Questions
              </Box>
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      <Box mt={3}>
        <Typography variant="h2" fontFamily="lato" fontWeight={100}>
          Are you sure you want to end the test?
        </Typography>
      </Box>
    </Box>
  );
};

// -------------------------------------------------------------
